import React, { useState, useEffect, useCallback } from 'react';
import { getIntegrations, revokeIntegration } from '../api/integrations';
import { IntegrationsResponse, IntegrationConnection } from '../types/integrations';
import { useToast } from '../context/ToastContext';
import {
  Cpu,
  Globe,
  Copy,
  Check,
  ShieldAlert,
  ShieldCheck,
  Ban,
  Clock,
  Key,
  Layers,
  RefreshCw,
  Info,
} from 'lucide-react';
import { Modal } from '../components/Modal';

export const IntegrationsPage: React.FC = () => {
  const { showToast } = useToast();
  const [data, setData] = useState<IntegrationsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [copiedKey, setCopiedKey] = useState<string | null>(null);
  const [revokingId, setRevokingId] = useState<string | null>(null);
  const [confirmRevokeTarget, setConfirmRevokeTarget] = useState<IntegrationConnection | null>(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getIntegrations();
      setData(res);
    } catch (err: any) {
      showToast(err.message || '加载 MCP/API 授权连接失败', 'error');
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleCopy = (text: string, key: string) => {
    navigator.clipboard.writeText(text);
    setCopiedKey(key);
    showToast('已复制到剪贴板', 'info');
    setTimeout(() => {
      setCopiedKey((curr) => (curr === key ? null : curr));
    }, 2000);
  };

  const handleRevokeConfirm = async () => {
    if (!confirmRevokeTarget) return;
    setRevokingId(confirmRevokeTarget.id);
    try {
      await revokeIntegration(confirmRevokeTarget.id);
      showToast(`已成功撤销对「${confirmRevokeTarget.client_name}」的授权`, 'success');
      setConfirmRevokeTarget(null);
      await loadData();
    } catch (err: any) {
      showToast(err.message || '撤销连接授权失败', 'error');
    } finally {
      setRevokingId(null);
    }
  };

  const formatDate = (dateStrOrNum: string | number | null | undefined) => {
    if (!dateStrOrNum) return '长期有效';
    try {
      const ms = typeof dateStrOrNum === 'number' ? dateStrOrNum * 1000 : Date.parse(dateStrOrNum);
      if (isNaN(ms)) return String(dateStrOrNum);
      const d = new Date(ms);
      return d.toLocaleString('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return String(dateStrOrNum);
    }
  };

  const mcpUrl = data?.mcp_url || 'https://en.lipluscu.com/mcp';
  const apiUrl = data?.api_url || 'https://en.lipluscu.com/api/v1';

  // Standard scope descriptions
  const scopeDefinitions: Array<{ id: string; name: string; desc: string }> = [
    {
      id: 'vocab:read',
      name: '读取所有业务',
      desc: '查询词条、词库结构、卡片状态、复习记录及用户配置等所有数据。',
    },
    {
      id: 'vocab:write',
      name: '词库/导入',
      desc: '新增、更新词条信息，管理词本归属，批量提交文件导入生词。',
    },
    {
      id: 'study:write',
      name: '评分/熟悉/卡片状态',
      desc: '提交卡片复习评分、执行撤销操作、登记熟悉度流水，变更卡片搁置/暂停。',
    },
    {
      id: 'settings:write',
      name: '改算法配置',
      desc: '修改 FSRS 算法参数、目标记忆率、学习步骤及学习日切分点。',
    },
    {
      id: 'data:export',
      name: '完整导出',
      desc: '触发词库与全量学习数据的离线 JSON 归档备份导出。',
    },
  ];

  return (
    <div className="max-w-6xl mx-auto px-4 py-8 sm:px-6 lg:px-8 space-y-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 border-b border-slate-200/80 pb-6">
        <div>
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-emerald-100/80 text-emerald-800 rounded-xl shadow-sm">
              <Cpu className="w-6 h-6" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-900 tracking-tight">
                MCP / API 授权与外部连接
              </h1>
              <p className="text-sm text-slate-500 mt-0.5">
                Model Context Protocol 开放服务与第三方 OAuth 2.0 客户端连接管理
              </p>
            </div>
          </div>
        </div>

        <button
          onClick={loadData}
          disabled={loading}
          className="inline-flex items-center gap-2 px-3.5 py-2 text-xs font-medium text-slate-700 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 disabled:opacity-50 transition-colors shadow-sm self-start sm:self-auto cursor-pointer"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
          刷新状态
        </button>
      </div>

      {/* Endpoints & Architecture Card */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* MCP Endpoint Card */}
        <div className="bg-white rounded-2xl border border-slate-200/80 shadow-sm p-6 flex flex-col justify-between">
          <div>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2 text-emerald-800 font-semibold text-sm">
                <Cpu className="w-4 h-4 text-emerald-600" />
                <span>Model Context Protocol (MCP) 地址</span>
              </div>
              <span className="text-[11px] font-medium bg-emerald-50 text-emerald-700 px-2 py-0.5 rounded-full border border-emerald-200">
                标准 MCP 规范
              </span>
            </div>

            <p className="text-xs text-slate-600 leading-relaxed mb-4">
              向 AI 宿主（如 Claude Desktop、Cursor 等）提供 API 粒度的工具集成。默认暴露核心学习工具，并支持通过 <code className="text-slate-800 bg-slate-100 px-1 py-0.5 rounded font-mono">discover</code> 与 <code className="text-slate-800 bg-slate-100 px-1 py-0.5 rounded font-mono">call_read / call_write</code> 按需调度其余完整业务 API。
            </p>

            <div className="bg-slate-50 rounded-xl p-3 border border-slate-200 flex items-center justify-between gap-3">
              <span className="font-mono text-xs text-slate-800 break-all select-all">
                {mcpUrl}
              </span>
              <button
                type="button"
                onClick={() => handleCopy(mcpUrl, 'mcp')}
                className="shrink-0 p-1.5 text-slate-500 hover:text-emerald-700 hover:bg-emerald-50 rounded-lg transition-colors border border-transparent hover:border-emerald-200 cursor-pointer"
                title="复制 MCP 服务地址"
              >
                {copiedKey === 'mcp' ? (
                  <Check className="w-4 h-4 text-emerald-600" />
                ) : (
                  <Copy className="w-4 h-4" />
                )}
              </button>
            </div>
          </div>

          <div className="mt-4 pt-3 border-t border-slate-100 flex items-center gap-2 text-[11px] text-slate-400">
            <Info className="w-3.5 h-3.5 text-slate-400 shrink-0" />
            <span>OAuth 授权流程严格基于通行密钥 (Passkey) 认证，不依赖手写 Token。</span>
          </div>
        </div>

        {/* REST API Endpoint Card */}
        <div className="bg-white rounded-2xl border border-slate-200/80 shadow-sm p-6 flex flex-col justify-between">
          <div>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2 text-slate-800 font-semibold text-sm">
                <Globe className="w-4 h-4 text-slate-600" />
                <span>REST API 基础根地址</span>
              </div>
              <span className="text-[11px] font-medium bg-slate-100 text-slate-700 px-2 py-0.5 rounded-full border border-slate-200">
                Bearer Token / OAuth
              </span>
            </div>

            <p className="text-xs text-slate-600 leading-relaxed mb-4">
              Lexi 标准 RESTful 接口。第三方客户端可在完成 OAuth 2.0 PKCE 授权后，使用颁发的 Bearer Access Token 在有效期与权限范围内调用词条、卡片及统计接口。
            </p>

            <div className="bg-slate-50 rounded-xl p-3 border border-slate-200 flex items-center justify-between gap-3">
              <span className="font-mono text-xs text-slate-800 break-all select-all">
                {apiUrl}
              </span>
              <button
                type="button"
                onClick={() => handleCopy(apiUrl, 'api')}
                className="shrink-0 p-1.5 text-slate-500 hover:text-slate-800 hover:bg-slate-200/60 rounded-lg transition-colors border border-transparent cursor-pointer"
                title="复制 REST API 地址"
              >
                {copiedKey === 'api' ? (
                  <Check className="w-4 h-4 text-emerald-600" />
                ) : (
                  <Copy className="w-4 h-4" />
                )}
              </button>
            </div>
          </div>

          <div className="mt-4 pt-3 border-t border-slate-100 flex items-center gap-2 text-[11px] text-slate-400">
            <ShieldCheck className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
            <span>所有请求受严格 CSRF 防护、访问频次限制及作用域校验保护。</span>
          </div>
        </div>
      </div>

      {/* Scope Permissions Card */}
      <div className="bg-white rounded-2xl border border-slate-200/80 shadow-sm p-6">
        <div className="flex items-center gap-2 text-slate-900 font-semibold text-base mb-2">
          <Layers className="w-5 h-5 text-emerald-600" />
          <span>OAuth 作用域规范 (Scopes)</span>
        </div>
        <p className="text-xs text-slate-500 mb-4">
          细粒度的权限边界划分，第三方应用仅能访问您明确勾选批准的权限范围：
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {scopeDefinitions.map((item) => (
            <div
              key={item.id}
              className="p-3.5 rounded-xl border border-slate-200 bg-slate-50/50 hover:bg-slate-50 transition-colors"
            >
              <div className="flex items-center justify-between gap-2 mb-1.5">
                <span className="font-mono text-xs font-bold text-slate-800 bg-white px-2 py-0.5 rounded border border-slate-200 shadow-2xs">
                  {item.id}
                </span>
                <span className="text-[11px] font-medium text-emerald-700 bg-emerald-50 px-1.5 py-0.2 rounded">
                  {item.name}
                </span>
              </div>
              <p className="text-xs text-slate-600 leading-relaxed">{item.desc}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Connected Applications List */}
      <div className="bg-white rounded-2xl border border-slate-200/80 shadow-sm overflow-hidden">
        <div className="px-6 py-5 border-b border-slate-200/80 flex items-center justify-between">
          <div>
            <h2 className="text-base font-bold text-slate-900">已授权客户端连接</h2>
            <p className="text-xs text-slate-500 mt-0.5">
              当前已被授权访问您 Lexi 数据的 MCP 宿主与第三方集成
            </p>
          </div>
          <span className="text-xs font-medium text-slate-500 bg-slate-100 px-2.5 py-1 rounded-full">
            共 {data?.connections.length || 0} 个授权记录
          </span>
        </div>

        {loading ? (
          <div className="py-12 flex flex-col items-center justify-center text-slate-400">
            <RefreshCw className="w-6 h-6 animate-spin mb-2 text-emerald-600" />
            <span className="text-xs font-medium">正在拉取连接列表...</span>
          </div>
        ) : !data || data.connections.length === 0 ? (
          <div className="py-12 px-6 text-center">
            <div className="w-12 h-12 rounded-2xl bg-slate-100 text-slate-400 flex items-center justify-center mx-auto mb-3">
              <Key className="w-6 h-6" />
            </div>
            <h3 className="text-sm font-semibold text-slate-800 mb-1">暂无外部应用连接</h3>
            <p className="text-xs text-slate-500 max-w-md mx-auto leading-relaxed">
              当您在外部 AI 宿主（如 Claude Desktop 或 Cursor）或开发者客户端中使用上方 MCP 地址发起连接时，将通过浏览器唤起 Passkey 认证与授权页面，连接将在成功后呈现在此处。
            </p>
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {data.connections.map((conn) => {
              const isRevoked = conn.revoked;
              const isExpired =
                conn.expires_at &&
                (typeof conn.expires_at === 'number'
                  ? conn.expires_at * 1000 < Date.now()
                  : Date.parse(conn.expires_at) < Date.now());

              return (
                <div
                  key={conn.id}
                  className={`p-6 transition-colors ${
                    isRevoked ? 'bg-slate-50/70' : 'hover:bg-slate-50/50'
                  }`}
                >
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="space-y-2">
                      <div className="flex items-center gap-3">
                        <span className="font-bold text-slate-900 text-base">
                          {conn.client_name || '外部 OAuth 客户端'}
                        </span>
                        {isRevoked ? (
                          <span className="inline-flex items-center gap-1 text-[11px] font-medium text-slate-500 bg-slate-200/80 px-2 py-0.5 rounded-full">
                            <Ban className="w-3 h-3" />
                            已撤销
                          </span>
                        ) : isExpired ? (
                          <span className="inline-flex items-center gap-1 text-[11px] font-medium text-amber-700 bg-amber-100/70 px-2 py-0.5 rounded-full">
                            <Clock className="w-3 h-3" />
                            已过期
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-[11px] font-medium text-emerald-700 bg-emerald-100/80 px-2 py-0.5 rounded-full">
                            <ShieldCheck className="w-3 h-3" />
                            活跃运行中
                          </span>
                        )}
                      </div>

                      {/* Scopes */}
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span className="text-xs text-slate-500 font-medium mr-1">授予权限:</span>
                        {conn.scopes.map((s) => (
                          <span
                            key={s}
                            className="font-mono text-[11px] bg-slate-100 text-slate-700 px-2 py-0.5 rounded border border-slate-200"
                            title={data.scopes[s] || s}
                          >
                            {s}
                          </span>
                        ))}
                      </div>

                      {/* Dates */}
                      <div className="flex items-center gap-4 text-xs text-slate-400">
                        <span>授权时间：{formatDate(conn.created_at)}</span>
                        <span>•</span>
                        <span>有效期至：{formatDate(conn.expires_at)}</span>
                      </div>
                    </div>

                    {/* Revoke Action */}
                    {!isRevoked && (
                      <div className="shrink-0">
                        <button
                          type="button"
                          onClick={() => setConfirmRevokeTarget(conn)}
                          className="px-3.5 py-1.5 text-xs font-medium text-red-600 hover:text-red-700 bg-white hover:bg-red-50 border border-slate-200 hover:border-red-200 rounded-lg transition-colors cursor-pointer"
                        >
                          撤销连接
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Revoke Confirmation Modal */}
      <Modal
        isOpen={!!confirmRevokeTarget}
        onClose={() => setConfirmRevokeTarget(null)}
        title="确认撤销连接授权"
      >
        <div className="space-y-4">
          <div className="flex items-start gap-3 bg-red-50 border border-red-200 rounded-xl p-3.5 text-xs text-red-800 leading-relaxed">
            <ShieldAlert className="w-5 h-5 text-red-600 shrink-0 mt-0.5" />
            <div>
              撤销后，客户端「<strong>{confirmRevokeTarget?.client_name}</strong>」将立即失去调用 Lexi MCP 及 REST API 的访问权限，已有凭据将即刻失效。
            </div>
          </div>

          <p className="text-xs text-slate-600">
            如果未来仍需连接该应用，您可在该客户端中重新发起 OAuth Passkey 授权。
          </p>

          <div className="flex items-center justify-end gap-3 pt-3">
            <button
              type="button"
              disabled={!!revokingId}
              onClick={() => setConfirmRevokeTarget(null)}
              className="px-4 py-2 text-sm text-slate-600 hover:text-slate-800 hover:bg-slate-100 rounded-lg transition-colors cursor-pointer"
            >
              取消
            </button>
            <button
              type="button"
              disabled={!!revokingId}
              onClick={handleRevokeConfirm}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 transition-colors shadow-sm cursor-pointer"
            >
              {revokingId ? '正在撤销...' : '确认撤销授权'}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};
